import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Heart, Gift, ExternalLink, ArrowRight, Sparkles, Coffee, Flame, Star } from 'lucide-react';

export default function SupportPage() {
  const [splash, setSplash] = useState(null);
  const [pay, setPay] = useState(null);
  const [floats, setFloats] = useState([]);

  useEffect(() => {
    api.get('/splash').then(r => setSplash(r.data)).catch(() => {});
    api.get('/settings/payments').then(r => setPay(r.data)).catch(() => {});
  }, []);

  if (!splash) return null;
  const vis = splash.sections_visible || {};
  if (vis.support_page === false) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-white" data-testid="support-disabled">
        <div className="text-center max-w-md p-8">
          <Heart className="mx-auto text-muted-kc mb-3" size={28}/>
          <h2 className="font-display uppercase text-3xl">This page is paused.</h2>
          <p className="mt-2 text-muted-kc">Mickey turned this section off for now. Check back later.</p>
          <Link to="/" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-ink hover:text-[color:var(--kc-gold-2)]">Go home <ArrowRight size={14}/></Link>
        </div>
      </div>
    );
  }

  const venmoLink = (amount = '') => {
    const handle = pay?.venmo_handle || 'mickeytrivett';
    const note = encodeURIComponent('Kickass Course tip');
    return `https://venmo.com/${handle}?txn=pay${amount ? `&amount=${amount}` : ''}&note=${note}`;
  };
  const cashappLink = (amount = '') => {
    const handle = pay?.cashapp_handle || 'mickeytrivett';
    return `https://cash.app/$${handle}${amount ? `/${amount}` : ''}`;
  };

  const popHeart = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const id = Math.random().toString(36).slice(2, 9);
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setFloats((arr) => [...arr, { id, x, y }]);
    setTimeout(() => setFloats((arr) => arr.filter(f => f.id !== id)), 1400);
  };

  return (
    <div className="bg-white min-h-screen" data-testid="support-page">
      {/* === HERO === */}
      <section className="relative overflow-hidden text-white py-16 md:py-24" style={{ background: 'linear-gradient(135deg, #0B0B0C 0%, #1f1708 60%, #0B0B0C 100%)' }} onClick={popHeart}>
        <div className="absolute inset-0 pointer-events-none kc-noise opacity-50"/>
        <div className="absolute inset-0 pointer-events-none opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(rgba(212,164,55,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(212,164,55,0.5) 1px, transparent 1px)', backgroundSize: '36px 36px' }}/>
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full pointer-events-none animate-pulse" style={{ background: 'radial-gradient(circle, rgba(212,164,55,0.16), transparent 70%)' }}/>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full pointer-events-none animate-pulse" style={{ background: 'radial-gradient(circle, rgba(212,164,55,0.12), transparent 70%)', animationDelay: '1.5s' }}/>

        {/* Click-anywhere heart bursts */}
        {floats.map(f => (
          <span key={f.id} className="absolute pointer-events-none animate-[supportHeart_1.4s_ease-out_forwards]" style={{ left: f.x, top: f.y, color: '#D4A437' }}>
            <Heart size={28} fill="currentColor"/>
          </span>
        ))}

        <div className="relative kc-container text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold" data-testid="support-eyebrow">
            <Sparkles size={12}/> {splash.support_eyebrow || "IF I'VE HELPED YOU"}
          </div>
          <h1 className="mt-5 font-display uppercase leading-[0.92]" style={{ fontSize: 'clamp(2.5rem, 7vw, 6rem)' }} data-testid="support-title">
            {splash.support_title_top || "I'D RATHER MAKE"}<br/>
            <span className="text-[color:var(--kc-gold)]">{splash.support_title_bottom || 'AN IMPACT THAN MONEY.'}</span>
          </h1>
          <p className="mt-6 text-lg text-white/80 whitespace-pre-line leading-relaxed" data-testid="support-intro">
            {splash.support_intro}
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.22em] text-[color:var(--kc-gold)] opacity-80 inline-flex items-center gap-2"><Heart size={12} fill="currentColor"/> Tap anywhere on this section</p>
        </div>
      </section>

      {/* === MICKEY'S NOTE === */}
      <section className="py-14 bg-white border-b border-kc">
        <div className="kc-container max-w-2xl text-center">
          <p className="text-lg text-ink-2 whitespace-pre-line leading-relaxed" data-testid="support-paragraphs">
            {splash.support_paragraphs}
          </p>
        </div>
      </section>

      {/* === TIP JAR === */}
      <section className="py-16 md:py-20 bg-surface-2" data-testid="support-tipjar">
        <div className="kc-container max-w-4xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold-2)] font-semibold"><Coffee size={12}/> The Tip Jar</div>
            <h2 className="mt-3 font-display uppercase" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>Drop something in the jar.</h2>
            <p className="mt-3 text-muted-kc">Pick an amount that feels right. Or write your own. Pick a method that works for you.</p>
          </div>

          {/* Preset amounts */}
          <PresetGrid pay={pay} venmoLink={venmoLink} cashappLink={cashappLink}/>

          {/* Custom amount + method selector */}
          <CustomTip pay={pay} venmoLink={venmoLink} cashappLink={cashappLink}/>

          {(!pay?.venmo_enabled && !pay?.cashapp_enabled && !pay?.stripe_enabled) && (
            <div className="mt-6 text-center text-sm text-muted-kc bg-white border border-kc rounded-[var(--kc-radius-md)] p-5" data-testid="support-no-methods">
              Tip methods are currently being updated. Check back soon, or use the Amazon wishlist below.
            </div>
          )}
        </div>
      </section>

      {/* === AMAZON WISHLIST === */}
      {(splash.support_amazon_wishlist_url || '').trim() && (
        <section className="py-16 bg-white" data-testid="support-amazon">
          <div className="kc-container max-w-3xl">
            <div className="relative overflow-hidden p-8 md:p-10 rounded-[var(--kc-radius-md)] border-2 border-[color:var(--kc-gold)] bg-gold-soft">
              <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full opacity-40 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(212,164,55,0.5), transparent 70%)' }}/>
              <div className="relative flex flex-col md:flex-row md:items-center gap-6">
                <div className="w-16 h-16 rounded-full bg-ink flex items-center justify-center flex-shrink-0">
                  <Gift size={24} className="text-[color:var(--kc-gold)]"/>
                </div>
                <div className="flex-1">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold-2)] font-semibold">An Alternative Path</div>
                  <h3 className="mt-1 font-display uppercase text-3xl leading-tight">My Amazon Wishlist</h3>
                  <p className="mt-2 text-ink-2">If sending money's not your thing, sending a piece of gear, a book, or something I'm working toward — same energy. Same gratitude.</p>
                </div>
                <a href={splash.support_amazon_wishlist_url} target="_blank" rel="noreferrer noopener" className="group h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90 transition-all hover:gap-3 flex-shrink-0" data-testid="support-amazon-btn">
                  {splash.support_amazon_button_label || 'View Amazon Wishlist'} <ExternalLink size={16}/>
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* === THANK YOU OUTRO === */}
      <section className="py-20 bg-ink text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none kc-noise opacity-40"/>
        <div className="relative kc-container max-w-2xl">
          <div className="flex justify-center gap-1 mb-6 text-[color:var(--kc-gold)]">
            {[0,1,2,3,4].map(i => <Star key={i} size={16} fill="currentColor" className={`animate-pulse`} style={{ animationDelay: `${i * 0.15}s` }}/>)}
          </div>
          <h2 className="font-display uppercase" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)' }} data-testid="support-thanks">
            {splash.support_thanks_message || 'Thank you. From me, to you — it lands.'}
          </h2>
          <p className="mt-5 text-white/65 text-sm">Whether you tip a dollar, a hundred, or zero — the work continues. The mission stays the same.</p>
          <div className="mt-7 inline-flex items-center gap-2 px-5 h-11 rounded-full border border-[color:var(--kc-gold)]/40 text-[color:var(--kc-gold)] text-xs uppercase tracking-[0.22em] font-semibold">
            <Flame size={14}/> Live life. Do good. Be happy.
          </div>
        </div>
      </section>

      <style>{`
        @keyframes supportHeart {
          0%   { transform: translate(-50%, -50%) scale(0.4); opacity: 0; }
          15%  { transform: translate(-50%, -60%) scale(1.3); opacity: 1; }
          100% { transform: translate(-50%, -180%) scale(0.7); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function PresetGrid({ pay, venmoLink, cashappLink }) {
  const amounts = [5, 10, 25, 50, 100];
  const [hover, setHover] = useState(null);
  const anyMethod = pay?.venmo_enabled || pay?.cashapp_enabled || pay?.stripe_enabled;
  if (!anyMethod) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="support-presets">
      {amounts.map((amt) => (
        <div
          key={amt}
          onMouseEnter={() => setHover(amt)}
          onMouseLeave={() => setHover(null)}
          className={`relative bg-white border-2 rounded-[var(--kc-radius-md)] transition-all overflow-hidden ${hover === amt ? 'border-[color:var(--kc-gold)] scale-[1.03] shadow-[0_15px_40px_-12px_rgba(212,164,55,0.4)]' : 'border-kc'}`}
          data-testid={`support-preset-${amt}`}
        >
          <div className="p-5 text-center">
            <div className="font-display text-4xl text-[color:var(--kc-gold-2)] tabular-nums leading-none">${amt}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-kc mt-1">{amt <= 10 ? 'Coffee level' : amt <= 25 ? 'Real respect' : amt <= 50 ? 'Hero move' : 'You\u2019re kind of nuts (in the best way)'}</div>
          </div>
          {hover === amt && (
            <div className="absolute inset-x-0 bottom-0 flex divide-x divide-white/20 bg-ink text-white text-[10px] uppercase tracking-wider font-semibold">
              {pay?.venmo_enabled && <a href={venmoLink(amt)} target="_blank" rel="noreferrer" className="flex-1 h-9 inline-flex items-center justify-center gap-1 hover:bg-black/80">Venmo</a>}
              {pay?.cashapp_enabled && <a href={cashappLink(amt)} target="_blank" rel="noreferrer" className="flex-1 h-9 inline-flex items-center justify-center gap-1 hover:bg-black/80">CashApp</a>}
              {pay?.stripe_enabled && pay?.stripe_link && <a href={pay.stripe_link} target="_blank" rel="noreferrer" className="flex-1 h-9 inline-flex items-center justify-center gap-1 hover:bg-black/80">Card</a>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CustomTip({ pay, venmoLink, cashappLink }) {
  const [amount, setAmount] = useState('');
  const anyMethod = pay?.venmo_enabled || pay?.cashapp_enabled || pay?.stripe_enabled;
  if (!anyMethod) return null;
  const safe = (parseFloat(amount) > 0 && parseFloat(amount) < 10000) ? parseFloat(amount).toFixed(2).replace(/\.00$/, '') : '';
  return (
    <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-5 mt-2" data-testid="support-custom">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Or a custom amount</label>
          <div className="mt-1 flex items-center gap-2">
            <span className="font-display text-2xl text-muted-kc">$</span>
            <input
              type="number"
              min="1"
              max="10000"
              step="0.01"
              placeholder="Any amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="flex-1 h-12 px-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white font-display text-2xl tabular-nums focus:outline-none focus:border-[color:var(--kc-gold)]"
              data-testid="support-custom-input"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {pay?.venmo_enabled && <a href={venmoLink(safe)} target="_blank" rel="noreferrer" className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90" data-testid="support-custom-venmo">Venmo</a>}
          {pay?.cashapp_enabled && <a href={cashappLink(safe)} target="_blank" rel="noreferrer" className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90" data-testid="support-custom-cashapp">CashApp</a>}
          {pay?.stripe_enabled && pay?.stripe_link && <a href={pay.stripe_link} target="_blank" rel="noreferrer" className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)]" data-testid="support-custom-stripe">Card</a>}
        </div>
      </div>
    </div>
  );
}
